import React, { useState, useEffect, useMemo } from 'react';
import { 
  X, Phone, Mail, MapPin, Calendar, Check, Clock,
  ChevronRight, ChevronLeft, CreditCard, User, Route, RefreshCw,
  Receipt, ExternalLink, PoundSterling, FileText, Navigation,
  CheckCircle2, Loader2, ChevronDown, Briefcase, ArrowLeft,
  MoreVertical, Pencil, SkipForward, MessageSquare, Send,
  Maximize2
} from 'lucide-react';
import JobEditModal from './JobEditModal';
import CustomerDetailModal from './CustomerDetailModal';
import CustomerPhotos from './CustomerPhotos';
import CustomerNotesEditor from './CustomerNotesEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Job Detail Panel - Slide-in side panel from the right
 * Keeps the work planner visible behind it
 */
const JobDetailPanel = ({ 
  job, 
  onClose, 
  onComplete, 
  onDoneAndPaid,
  onReschedule, 
  onSkip,
  onNavigate,
  onViewCustomer,
  onUndoComplete,
  onViewInvoice,
  onUpdateJob,
  onEditJob,
  getStaffColor,
  staffList = [],
  roundsList = [],
  isWorker = false,
  isOpen = true,
  workerPermissions = {},
  userSettings = {},
  showActions = true,
  zIndex = null
}) => {
  const [customerData, setCustomerData] = useState(null);
  const [invoiceData, setInvoiceData] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editFrequency, setEditFrequency] = useState({ weeks: 1, day: 'Monday' });
  const [editDate, setEditDate] = useState('');
  const [priceApplyMode, setPriceApplyMode] = useState(null);
  // Generic apply-scope state used by ALL non-price edits (service, assignee,
  // frequency, round) to ask the user whether the change is just for this
  // occurrence or for all future occurrences of the recurring job.
  // Values: null (not yet asked) | 'pick-scope' (showing the scope picker)
  const [applyScopeMode, setApplyScopeMode] = useState(null);
  const [userRounds, setUserRounds] = useState([]);
  const [userServices, setUserServices] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [showMenuDropdown, setShowMenuDropdown] = useState(false);
  const [showSkipModal, setShowSkipModal] = useState(false);
  const [showEditJobModal, setShowEditJobModal] = useState(false);
  const [showCustomerProfile, setShowCustomerProfile] = useState(false);
  const [selectedDateIndex, setSelectedDateIndex] = useState(0);
  const [siblingRecurring, setSiblingRecurring] = useState(null);
  const [jobHistory, setJobHistory] = useState([]);
  const [fullCustomerData, setFullCustomerData] = useState(null);
  const [loadingCustomer, setLoadingCustomer] = useState(false);
  // (May 2026) Removed the live `userPosition` GPS watcher that used to feed
  // a /directions iframe — see the "Mini Map" comment further down for the
  // rationale. The watcher was using `navigator.geolocation.watchPosition`
  // which keeps the GPS chip warm on iOS; killing it gives workers a small
  // battery win on a long round.
  
  // Charge confirmation modal (mirrors SwipeableJobCard so JobDetailPanel and the
  // WorkPlanner inline list show the same Done/Skip/Paid behaviour).
  const [showChargeConfirmModal, setShowChargeConfirmModal] = useState(false);
  const [chargeAmount, setChargeAmount] = useState(0);
  const [pendingSendMethod, setPendingSendMethod] = useState(null);
  const [vatSettings, setVatSettings] = useState({ enabled: false, percentage: 20 });
  // Invoice-channel chooser shown when the customer has no Stripe/GoCardless
  // saved. Mirrors the WorkPlanner inline "Mark as Complete?" modal so
  // workers (and owners) see a consistent send-invoice flow on the detail panel.
  const [showInvoiceSendModal, setShowInvoiceSendModal] = useState(false);
  // Expanded map modal — tap the mini-map to enlarge it. The mini-map is
  // 144px tall which is fine at-a-glance but useless for inspecting access
  // details, neighbouring properties, or parking. Opens a full-viewport
  // overlay with the same /place embed at a higher zoom.
  const [showExpandedMap, setShowExpandedMap] = useState(false);
  // Expanded map view mode — 'place' (overhead map with pin) or 'streetview'
  // (ground-level photo). Street View helps workers arriving at a new
  // address pre-check what the front gate looks like before they pull up.
  // Requires the customer's saved lat/lng; the toggle button is hidden
  // when we don't have coordinates (Street View embed can't geocode text
  // addresses on its own).
  const [mapMode, setMapMode] = useState('place');

  // Handle animation on open/close
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
      setTimeout(() => setShouldRender(false), 300);
    }
  }, [isOpen]);

  // Fetch customer data
  useEffect(() => {
    const fetchCustomer = async () => {
      const customerId = job?.customerRef || job?.customer_id || job?.customerId;
      if (!customerId) return;
      
      try {
        const token = localStorage.getItem('streakfree_token');
        const res = await fetch(`${API_URL}/api/customers/${customerId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setCustomerData(data);
        }
      } catch (err) {
        console.error('Error fetching customer:', err);
      }
    };
    if (job) fetchCustomer();
  }, [job]);

  // Canvassing "first clean" jobs are one-offs — the ongoing schedule lives on
  // a separate recurring job. Fetch it so the date arrows can show next visits.
  useEffect(() => {
    setSiblingRecurring(null);
    const customerId = job?.customerRef || job?.customer_id || job?.customerId;
    const ownFreq = String(job?.frequency || '').trim();
    if (!customerId || ownFreq) return;
    const fetchSibling = async () => {
      try {
        const token = localStorage.getItem('streakfree_token');
        const res = await fetch(`${API_URL}/api/jobs?customer_id=${customerId}&limit=50`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) return;
        const jobs = await res.json();
        const candidates = jobs.filter(j =>
          j.id !== job.id &&
          String(j.frequency || '').trim() &&
          ['pending', 'scheduled'].includes((j.status || '').toLowerCase()) &&
          j.scheduled_date
        ).sort((a, b) => (a.scheduled_date || '').localeCompare(b.scheduled_date || ''));
        if (candidates.length) setSiblingRecurring(candidates[0]);
      } catch (e) { /* non-critical */ }
    };
    fetchSibling();
  }, [job?.id]);

  // Fetch user's rounds and services
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem('streakfree_token');
        
        const roundsRes = await fetch(`${API_URL}/api/rounds`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (roundsRes.ok) {
          const data = await roundsRes.json();
          setUserRounds(data.map(r => r.name));
        }
        
        const servicesRes = await fetch(`${API_URL}/api/services`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (servicesRes.ok) {
          const data = await servicesRes.json();
          setUserServices(data.map(s => s.name || s));
        }

        // Fetch VAT settings so the charge-confirm modal can show the same
        // breakdown as the WorkPlanner inline list.
        const settingsRes = await fetch(`${API_URL}/api/settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (settingsRes.ok) {
          const settings = await settingsRes.json();
          setVatSettings({
            enabled: settings.vat_enabled || false,
            percentage: settings.vat_percentage || 20,
          });
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
      }
    };
    fetchUserData();
  }, []);

  // Fetch invoice data for completed jobs
  useEffect(() => {
    const fetchInvoiceData = async () => {
      if (!job || (job.status !== 'completed' && job.status !== 'done')) return;
      
      try {
        const token = localStorage.getItem('streakfree_token');
        const res = await fetch(`${API_URL}/api/invoices?job_id=${job.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const invoices = await res.json();
          if (invoices.length > 0) setInvoiceData(invoices[0]);
        }
      } catch (err) {
        console.error('Error fetching invoice:', err);
      }
    };
    if (job) fetchInvoiceData();
  }, [job]);

  // Available rounds for selection - must be before early return
  const availableRounds = useMemo(() => {
    const rounds = new Set(userRounds);
    if (roundsList?.length > 0) roundsList.forEach(r => rounds.add(r.name || r));
    if (job?.round_name) rounds.add(job.round_name);
    if (job?.customer_zone) rounds.add(job.customer_zone);
    if (job?.zone) rounds.add(job.zone);
    return [...rounds].filter(Boolean).sort();
  }, [userRounds, roundsList, job?.round_name, job?.customer_zone, job?.zone]);

  // Available services - must be before early return
  const availableServices = useMemo(() => {
    const services = new Set(userServices);
    services.add('Window Clean');
    services.add('Gutter Clean');
    services.add('Conservatory Clean');
    services.add('Fascia & Soffit');
    services.add('Pressure Wash');
    if (job?.services) services.add(job.services);
    return [...services].filter(Boolean).sort();
  }, [userServices, job?.services]);

  if (!shouldRender || !job) return null;

  // ── Payment-method detection (mirrors WorkPlanner SwipeableJobCard) ──
  // The Done button should pop the charge-confirm modal whenever the customer
  // has Stripe card-on-file or an active GoCardless mandate. Workers never
  // see the auto-charge confirm — they always go through the standard "send
  // invoice" flow.
  const paymentTypeRaw = (
    customerData?.payment_method ||
    job.customer_payment_method ||
    ''
  ).toLowerCase();
  const hasStripe =
    customerData?.stripe_has_payment_method === true ||
    paymentTypeRaw.includes('stripe') ||
    paymentTypeRaw.includes('card');
  const hasGoCardlessMandate =
    Boolean(customerData?.gocardless_mandate_id) &&
    !['cancelled', 'failed', 'expired'].includes(
      (customerData?.gocardless_mandate_status || '').toLowerCase()
    );
  const hasGoCardless =
    customerData?.has_gocardless === true ||
    hasGoCardlessMandate ||
    paymentTypeRaw.includes('gocardless') ||
    paymentTypeRaw.includes('direct debit');
  // Worker auto-charge gate: workers may trigger Stripe/GoCardless on the
  // customer's saved method ONLY when the owner has toggled
  // `can_take_payments` ON in Settings → Worker Permissions. Owners always
  // pass. The full flow — including the failure path (invoice unpaid,
  // added to Owings, sent to customer, owner emailed) — mirrors the
  // owner's exactly so Amber's permitted Done press behaves identically
  // to Worthing's. See `routes/jobs.py:complete_job` for the matching
  // backend gate + the Stripe failure followup that fires alongside.
  const canChargeCustomer = !isWorker || workerPermissions?.can_take_payments;
  const hasPaymentMethod = canChargeCustomer && (hasStripe || hasGoCardless);
  const paymentTypeLabel = hasStripe ? 'Stripe' : hasGoCardless ? 'GoCardless' : null;

  const calculateTotalWithVat = (price) => {
    if (!vatSettings.enabled || !vatSettings.percentage) return price;
    return Math.round(price * (1 + vatSettings.percentage / 100) * 100) / 100;
  };

  const handleDoneClick = () => {
    const price = job.price || 0;
    // HANDOVER MODE — rounds sold to another company. Mark Done silently:
    // no charge, no invoice, no SMS/Email. Backend's complete_job enforces
    // this regardless; we bypass the modal here for the expected UX.
    // EXCEPTIONS — these run normal flow so the owner can still invoice:
    //   • One-off / Ad-hoc jobs (per user: not part of the rounds sale)
    //   • Explicit bypass_handover_mode=true on the job
    const _freqLc = (job.frequency || '').toLowerCase();
    const _jobIsOneOff = !!job.is_one_off || ['one-off','one off','ad hoc','ad-hoc'].some(t => _freqLc.includes(t));
    const _handoverSilent = !!userSettings?.handover_mode && !job.bypass_handover_mode && !_jobIsOneOff;
    if (_handoverSilent) {
      onComplete && onComplete(job, {
        sendMethod: 'none',
        hasPaymentMethod: false,
        paymentType: null,
      });
      return;
    }
    // £0 jobs: short-circuit straight to "mark done, no invoice, no notify"
    // (matches user's Apr 2026 requirement). Backend already enforces this
    // at /api/jobs/{id}/complete, but bypassing the modal here is the cleanest UX.
    if (price === 0) {
      onComplete && onComplete(job, { sendMethod: 'none', hasPaymentMethod: false, chargeAmount: 0 });
      return;
    }
    if (hasPaymentMethod) {
      // Show charge-confirm modal (Stripe/GC customers get charged on confirm,
      // never receive an invoice email/SMS — same as the inline list flow).
      setChargeAmount(calculateTotalWithVat(price));
      setPendingSendMethod('none');
      setShowChargeConfirmModal(true);
      return;
    }
    // No payment method → show the channel-chooser modal so the user (owner
    // or worker) can decide how the invoice is dispatched. This matches the
    // WorkPlanner inline "Mark as Complete?" modal and is the experience the
    // owner asked for in the May 2026 worker-payment ticket: "if the customer
    // doesn't [have Stripe/GC] then they should press done and the modal
    // come up about sending the invoice".
    setShowInvoiceSendModal(true);
  };

  const handleSendInvoiceAndComplete = (sendMethod) => {
    setShowInvoiceSendModal(false);
    onComplete && onComplete(job, {
      sendMethod,
      hasPaymentMethod: false,
      paymentType: null,
    });
  };

  const handleConfirmCharge = () => {
    setShowChargeConfirmModal(false);
    onComplete && onComplete(job, {
      sendMethod: pendingSendMethod || 'none',
      hasPaymentMethod,
      paymentType: paymentTypeLabel,
      chargeAmount,
    });
  };

  const handleSendInsteadOfCharge = (sendMethod) => {
    setShowChargeConfirmModal(false);
    // "Send Invoice instead" — bypass auto-charge entirely and send the
    // invoice via the chosen channel. CRITICAL: must pass
    // `skipAutoCharge: true` so the backend doesn't attempt Stripe/GC on
    // top of sending the invoice. Without this we'd both charge AND
    // notify, doubling up on the customer.
    //
    // Also setting `hasPaymentMethod: false` here so the WorkPlanner's
    // optimistic UI doesn't flash "paid" — the user explicitly asked for
    // an unpaid invoice flow.
    onComplete && onComplete(job, {
      sendMethod,
      hasPaymentMethod: false,
      paymentType: null,
      skipAutoCharge: true,
    });
  };
  const status = job.status || 'scheduled';
  const isCompleted = status === 'completed' || status === 'done';
  const isPaid = status === 'paid' || job.invoice_paid || (invoiceData?.status === 'paid');

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  // Format frequency display
  const formatFrequency = () => {
    const freq = job.frequency || customerData?.frequency;
    if (!freq || freq === 'one-off' || freq.toLowerCase() === 'ad hoc') return 'One-off';
    
    if (freq.toLowerCase().includes(' on ')) {
      return freq.charAt(0).toUpperCase() + freq.slice(1);
    }
    
    const weeksMatch = freq.match(/(\d+)\s*week/i);
    const weeks = weeksMatch ? parseInt(weeksMatch[1]) : null;
    const dayOfWeek = job.preferred_day || customerData?.preferred_day || '';
    
    if (weeks) {
      if (dayOfWeek) return `Every ${weeks} week${weeks > 1 ? 's' : ''} on ${dayOfWeek}`;
      return `Every ${weeks} week${weeks > 1 ? 's' : ''}`;
    }
    
    return freq.charAt(0).toUpperCase() + freq.slice(1);
  };

  // Parse frequency for editing
  const parseFrequency = () => {
    const freq = job.frequency || customerData?.frequency || '';
    let weeks = 4;
    let day = '';
    
    if (!freq || freq === 'one-off' || freq.toLowerCase() === 'ad hoc') {
      return { weeks: 0, day: '' };
    }
    
    const fullMatch = freq.toLowerCase().match(/(?:every\s+)?(\d+)\s*weeks?\s*(?:on\s+)?(\w+)?/i);
    if (fullMatch) {
      weeks = parseInt(fullMatch[1]) || 4;
      if (fullMatch[2]) {
        const dayNames = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
        const possibleDay = fullMatch[2].toLowerCase();
        if (dayNames.includes(possibleDay)) {
          day = possibleDay.charAt(0).toUpperCase() + possibleDay.slice(1);
        }
      }
    }
    
    if (!day) {
      const preferredDay = job.preferred_day || customerData?.preferred_day;
      if (preferredDay) {
        day = preferredDay.charAt(0).toUpperCase() + preferredDay.slice(1).toLowerCase();
      }
    }
    
    return { weeks, day };
  };

  // Handle edit button clicks
  const handleEdit = (field) => {
    setEditingField(field);
    
    if (field === 'price') {
      setEditValue(String(job.price || 0));
      setPriceApplyMode(null);
    } else if (field === 'frequency') {
      const parsed = parseFrequency();
      setEditFrequency(parsed);
      setEditDate(job.scheduled_date || '');
    } else if (field === 'assignee') {
      setEditValue(job.assignee || '');
    } else if (field === 'round') {
      setEditValue(job.round_name || job.customer_zone || job.zone || job.round || '');
    } else if (field === 'services') {
      setEditValue(job.services || job.service || 'Window Clean');
    }
  };

  // Save edit — wraps onUpdateJob with the correct payload for whichever
  // field is being edited. `applyMode` is "this" (just this occurrence) or
  // "all" (also propagate to all future jobs of the same recurring series).
  // The actual scope question is asked via the confirmation step inserted
  // before this function runs, so we just trust the mode here.
  const handleSaveEdit = async (mode = 'this') => {
    if (!onUpdateJob) {
      setEditingField(null);
      return;
    }

    let updateData = {};
    
    if (editingField === 'assignee') {
      updateData = { assignee: editValue };
    } else if (editingField === 'round') {
      // Round is stored on the CUSTOMER record as `zone`, not on the job.
      // Pushing it onto the job is silently dropped by the backend (the
      // JobUpdate model doesn't have `round_name`), so the change appears
      // to "not save". Push it via a special key the WorkPlanner handler
      // intercepts to update the customer instead.
      updateData = {
        _customer_zone: editValue,
        // Also update the job's `zone` field so any historical filter that
        // joins on job.zone still works for this single occurrence.
        zone: editValue,
        round_name: editValue,
      };
    } else if (editingField === 'frequency') {
      const freqStr = editFrequency.weeks > 0 
        ? `${editFrequency.weeks} weeks` + (editFrequency.day ? ` on ${editFrequency.day}` : '')
        : 'one-off';
      updateData = { 
        frequency: freqStr,
        preferred_day: editFrequency.day,
        scheduled_date: editDate || job.scheduled_date
      };
    } else if (editingField === 'services') {
      updateData = { services: editValue, service: editValue };
    }
    
    await onUpdateJob(job.id, updateData, mode === 'all');
    setEditingField(null);
    setApplyScopeMode(null);
  };

  // Save price with option to apply to future jobs
  const handleSavePrice = async (mode) => {
    if (!onUpdateJob) {
      setEditingField(null);
      setPriceApplyMode(null);
      return;
    }

    const newPrice = parseFloat(editValue) || 0;
    await onUpdateJob(job.id, { price: newPrice }, mode === 'all');
    setEditingField(null);
    setPriceApplyMode(null);
  };

  // Handle close with animation
  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => onClose(), 300);
  };

  // Render Edit Modal
  const renderEditModal = () => {
    if (!editingField) return null;

    const getTitle = () => {
      switch (editingField) {
        case 'price': return 'Edit Price';
        case 'assignee': return 'Change Assignee';
        case 'round': return 'Change Round';
        case 'frequency': return 'Edit Schedule';
        case 'services': return 'Select Service';
        default: return 'Edit';
      }
    };

    // Scope-picker step: asks "Just this once or repeat every time?" before
    // saving a service change on a recurring job. Mirrors handleSavePrice UX.
    const renderApplyScopeStep = (scopeKind) => {
      const labelMap = {
        service: 'Apply this service to:',
      };
      return (
        <div className="p-5 space-y-3" data-testid={`apply-scope-step-${scopeKind}`}>
          <p className="text-center text-slate-600 mb-3">
            {labelMap[scopeKind] || 'Apply this change to:'}
          </p>
          <button
            onClick={() => handleSaveEdit('this')}
            className="w-full py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold transition-colors"
            data-testid={`apply-scope-just-this-${scopeKind}`}
          >
            Just This Job
          </button>
          <button
            onClick={() => handleSaveEdit('all')}
            className="w-full py-3 bg-teal-500 hover:bg-teal-600 text-white rounded-xl font-semibold transition-colors"
            data-testid={`apply-scope-all-future-${scopeKind}`}
          >
            All Future Jobs
          </button>
          <button
            onClick={() => setApplyScopeMode(null)}
            className="w-full py-2 text-slate-500 hover:text-slate-700 font-medium"
            data-testid={`apply-scope-back-${scopeKind}`}
          >
            Back
          </button>
        </div>
      );
    };

    // Price modal with confirmation step
    if (editingField === 'price') {
      return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setEditingField(null)}>
          <div className="bg-white rounded-2xl w-full max-w-sm mx-4 overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-100">
              <h3 className="font-bold text-lg text-slate-900">{getTitle()}</h3>
            </div>
            <div className="p-5">
              {!priceApplyMode ? (
                <div className="space-y-4">
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl text-slate-400">£</span>
                    <input
                      type="number"
                      step="0.01"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 text-xl font-semibold border-2 border-slate-200 rounded-xl focus:border-teal-500 focus:ring-0 transition-colors"
                      autoFocus
                    />
                  </div>
                  <button
                    onClick={() => setPriceApplyMode('confirm')}
                    className="w-full py-3 bg-teal-500 hover:bg-teal-600 text-white rounded-xl font-semibold transition-colors"
                  >
                    Continue
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-center text-slate-600 mb-3">Apply this price to:</p>
                  <button
                    onClick={() => handleSavePrice('this')}
                    className="w-full py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold transition-colors"
                  >
                    Just This Job
                  </button>
                  <button
                    onClick={() => handleSavePrice('all')}
                    className="w-full py-3 bg-teal-500 hover:bg-teal-600 text-white rounded-xl font-semibold transition-colors"
                  >
                    All Future Jobs
                  </button>
                  <button
                    onClick={() => setPriceApplyMode(null)}
                    className="w-full py-2 text-slate-500 hover:text-slate-700 font-medium"
                  >
                    Back
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    // Frequency/Schedule modal
    if (editingField === 'frequency') {
      return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => { setEditingField(null); setApplyScopeMode(null); }}>
          <div className="bg-white rounded-2xl w-full max-w-sm mx-4 overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-100">
              <h3 className="font-bold text-lg text-slate-900">{getTitle()}</h3>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Next Date</label>
                <input
                  type="date"
                  value={editDate}
                  onChange={(e) => setEditDate(e.target.value)}
                  className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-lg focus:border-teal-500 focus:ring-0 transition-colors"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Frequency</label>
                {/* "One-off" is encoded as weeks=0; handleSaveEdit converts
                    that to the string "one-off" which the backend
                    (parse_frequency in services/job_logic.py) recognises as
                    non-recurring and stops projecting future dates for. */}
                <div className="flex items-center gap-2">
                  <span className="text-slate-500 text-sm">Every</span>
                  <select
                    value={editFrequency.weeks}
                    onChange={(e) => setEditFrequency(prev => ({ ...prev, weeks: parseInt(e.target.value) }))}
                    className="flex-1 px-3 py-2.5 border-2 border-slate-200 rounded-lg focus:border-teal-500"
                    data-testid="frequency-weeks-select"
                  >
                    <option value={0}>One-off (no repeat)</option>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(n => (
                      <option key={n} value={n}>{n} week{n > 1 ? 's' : ''}</option>
                    ))}
                  </select>
                </div>
                {editFrequency.weeks === 0 && (
                  <p className="text-xs text-slate-500 mt-1.5">
                    This job won&apos;t repeat after the next date.
                  </p>
                )}
              </div>
              
              {/* Preferred Day is irrelevant for a one-off — hide it so the
                  user doesn't think they need to pick something. */}
              {editFrequency.weeks > 0 && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Preferred Day</label>
                <select
                  value={editFrequency.day}
                  onChange={(e) => setEditFrequency(prev => ({ ...prev, day: e.target.value }))}
                  className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-lg focus:border-teal-500"
                >
                  <option value="">Any Day</option>
                  {daysOfWeek.map(day => (
                    <option key={day} value={day}>{day}</option>
                  ))}
                </select>
              </div>
              )}
            </div>
            <div className="px-5 py-4 bg-slate-50 flex gap-3">
              <button
                onClick={() => setEditingField(null)}
                className="flex-1 py-2.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg font-semibold transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleSaveEdit('all')}
                className="flex-1 py-2.5 bg-teal-500 hover:bg-teal-600 text-white rounded-lg font-semibold transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Services selection modal
    if (editingField === 'services') {
      return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => { setEditingField(null); setApplyScopeMode(null); }}>
          <div className="bg-white rounded-2xl w-full max-w-sm mx-4 overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-100">
              <h3 className="font-bold text-lg text-slate-900">{getTitle()}</h3>
            </div>
            {applyScopeMode === 'pick-scope' ? (
              renderApplyScopeStep('service')
            ) : (
            <>
            <div className="p-4 max-h-[50vh] overflow-y-auto">
              <div className="space-y-2">
                {availableServices.map((service, idx) => (
                  <button
                    key={idx}
                    onClick={() => setEditValue(service)}
                    className={`w-full px-4 py-3 text-left rounded-lg transition-all text-sm ${
                      editValue === service 
                        ? 'bg-teal-50 text-teal-700 font-semibold border-2 border-teal-500' 
                        : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border-2 border-transparent'
                    }`}
                  >
                    {service}
                  </button>
                ))}
              </div>
            </div>
            <div className="px-5 py-4 bg-slate-50 flex gap-3">
              <button
                onClick={() => setEditingField(null)}
                className="flex-1 py-2.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg font-semibold transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => setApplyScopeMode('pick-scope')}
                className="flex-1 py-2.5 bg-teal-500 hover:bg-teal-600 text-white rounded-lg font-semibold transition-colors"
              >
                Save
              </button>
            </div>
            </>
            )}
          </div>
        </div>
      );
    }

    // Assignee / Round selection modal
    const options = editingField === 'assignee' 
      ? (staffList.length > 0 ? [...staffList, 'Unassigned'] : ['Unassigned'])
      : availableRounds;

    return (
      <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => { setEditingField(null); setApplyScopeMode(null); }}>
        <div className="bg-white rounded-2xl w-full max-w-sm mx-4 overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
          <div className="px-5 py-4 border-b border-slate-100">
            <h3 className="font-bold text-lg text-slate-900">{getTitle()}</h3>
          </div>
          <div className="p-4 max-h-[50vh] overflow-y-auto">
            {options.length > 0 ? (
              <div className="space-y-2">
                {options.map((option, idx) => (
                  <button
                    key={idx}
                    onClick={() => setEditValue(option)}
                    className={`w-full px-4 py-3 text-left rounded-lg transition-all text-sm ${
                      editValue === option 
                        ? 'bg-teal-50 text-teal-700 font-semibold border-2 border-teal-500' 
                        : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border-2 border-transparent'
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            ) : (
              <input
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg"
                placeholder={`Enter ${editingField}...`}
              />
            )}
          </div>
          <div className="px-5 py-4 bg-slate-50 flex gap-3">
            <button
              onClick={() => setEditingField(null)}
              className="flex-1 py-2.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg font-semibold transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => handleSaveEdit(editingField === 'round' ? 'all' : 'this')}
              className="flex-1 py-2.5 bg-teal-500 hover:bg-teal-600 text-white rounded-lg font-semibold transition-colors"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Payment Method Modal
  const PaymentMethodModal = () => {
    const paymentMethods = [
      { id: 'cash', name: 'Cash', color: '#10b981' },
      { id: 'bank_transfer', name: 'Bank Transfer', color: '#3b82f6' },
      { id: 'card', name: 'Card', color: '#8b5cf6' },
      { id: 'cheque', name: 'Cheque', color: '#f59e0b' },
      { id: 'other', name: 'Other', color: '#6b7280' },
    ];

    return (
      <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowPaymentModal(false)}>
        <div className="bg-white rounded-2xl w-full max-w-sm mx-4 overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
          <div className="px-5 py-4 border-b border-slate-100 text-center">
            <h3 className="font-bold text-lg text-slate-900">How was this paid?</h3>
            <p className="text-sm text-slate-500 mt-1">
              {job.customer_name} - £{(job.price || 0).toFixed(2)}
            </p>
          </div>
          
          <div className="p-4 space-y-2">
            {paymentMethods.map(({ id, name, color }) => (
              <button
                key={id}
                onClick={() => {
                  setShowPaymentModal(false);
                  onDoneAndPaid && onDoneAndPaid(job, { paymentMethod: id });
                }}
                className="w-full flex items-center gap-3 p-3 rounded-xl transition-all hover:scale-[1.02] active:scale-[0.98]"
                style={{ backgroundColor: `${color}15` }}
              >
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}25` }}>
                  <CreditCard size={18} style={{ color }} />
                </div>
                <span className="font-medium text-slate-700">{name}</span>
              </button>
            ))}
          </div>
          
          <div className="px-5 py-3">
            <button
              onClick={() => setShowPaymentModal(false)}
              className="w-full py-2.5 text-slate-500 font-medium hover:text-slate-700"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Backdrop - click to close */}
      <div 
        className={`fixed inset-0 z-40 bg-black/20 transition-opacity duration-300 ${isAnimating ? 'opacity-100' : 'opacity-0'}`}
        style={zIndex ? { zIndex: zIndex - 1 } : undefined}
        onClick={handleClose}
      />
      
      {/* Side Panel - slides in from right */}
      <div 
        className={`fixed top-0 right-0 bottom-0 z-50 w-full max-w-md bg-white shadow-2xl transform transition-transform duration-300 ease-out flex flex-col ${
          isAnimating ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={zIndex ? { zIndex } : undefined}
        data-testid="job-detail-panel"
      >
        {/* Header - with safe area padding for iOS notch */}
        <div 
          className="bg-slate-800 text-white px-4 pb-4 flex-shrink-0"
          style={{ paddingTop: 'max(env(safe-area-inset-top, 16px), 16px)' }}
        >
          <div className="flex items-start justify-between mb-3">
            <button 
              onClick={handleClose} 
              className="p-1.5 -ml-1.5 hover:bg-white/10 rounded-lg transition-colors flex items-center gap-1.5"
              data-testid="close-panel-btn"
            >
              <ArrowLeft size={20} />
              <span className="text-sm font-medium">Back</span>
            </button>
            
            <div className="flex items-start gap-3">
              <div className="text-right">
                <p className="text-2xl font-bold">£{(job.price || 0).toFixed(2)}</p>
                <p className="text-xs text-slate-300">{job.services || 'Window Clean'}</p>
              </div>
              
              {/* 3-dot Menu */}
              <div className="relative">
                <button
                  onClick={() => setShowMenuDropdown(!showMenuDropdown)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  data-testid="job-menu-btn"
                >
                  <MoreVertical size={20} />
                </button>
                
                {/* Dropdown Menu */}
                {showMenuDropdown && (
                  <>
                    <div 
                      className="fixed inset-0 z-50"
                      onClick={() => setShowMenuDropdown(false)}
                    />
                    <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-xl shadow-xl z-50 overflow-hidden border border-slate-200">
                      <button
                        onClick={() => {
                          setShowMenuDropdown(false);
                          setShowEditJobModal(true);
                        }}
                        className="w-full px-4 py-3 text-left text-slate-700 hover:bg-slate-50 flex items-center gap-3 transition-colors"
                        data-testid="edit-job-btn"
                      >
                        <Pencil size={18} className="text-slate-500" />
                        <span className="font-medium">Edit Job</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          
          <button 
            onClick={() => onNavigate && onNavigate(job)}
            className="text-base font-semibold leading-tight text-left hover:text-blue-200 transition-colors flex items-center gap-1.5 group"
            data-testid="address-navigate-btn"
          >
            <MapPin size={14} className="text-white/70 group-hover:text-blue-300" />
            <span className="underline decoration-dashed underline-offset-2">
              {job.job_address || job.address_override || job.customer_address || job.address || 'Job Details'}
            </span>
          </button>
          
          {/* Tags */}
          <div className="flex flex-wrap gap-1.5 mt-2">
            {job.assignee && (
              <span className="px-2 py-1 bg-white/20 rounded text-xs font-medium">
                {job.assignee}
              </span>
            )}
            {(job.round_name || job.customer_zone || job.zone) && (
              <span className="px-2 py-1 bg-white/20 rounded text-xs font-medium">
                {job.round_name || job.customer_zone || job.zone}
              </span>
            )}
            {isCompleted && (
              <span className="px-2 py-1 bg-teal-500/30 rounded text-xs font-medium flex items-center gap-1">
                <Check size={12} /> Done
              </span>
            )}
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto bg-slate-100">
          {/* Hero Section - Price + Map */}
          <div className="relative">
            {/* Mini Map — always renders a single pin at the JOB address.
                We deliberately do NOT use Google's /directions embed here
                (May 2026 user feedback) because:
                  * the floating Directions button below already handles the
                    "I want turn-by-turn from where I am" intent
                  * the directions embed used to draw a 28-mile route line
                    from the worker's last GPS lock all the way to the job,
                    obscuring the actual pin location with a thick blue line
                    and a "44 min · 28.5 miles" callout that's noise in this
                    context (the user is browsing a customer profile, not
                    asking for directions).
                A static /place pin keeps the map purely informational —
                "this is where Miles Fox lives".

                Tap to expand: a transparent button above the iframe opens
                a full-viewport view. We need the button because Google's
                embed iframe captures pointer events for its own pan/zoom
                UI, so a click handler on the wrapping <div> would never
                fire. The expand button sits ABOVE the iframe with
                pointer-events: auto and the iframe gets pointer-events:
                none in the small view — accepted trade-off: in the
                mini-view, the user can't pan/zoom the embed, but they
                can tap to expand and pan/zoom in the big view. (The
                old mini-map allowed pan/zoom but most users never used
                that — they wanted bigger maps, hence this feature.) */}
            <div className="h-36 bg-slate-200 relative">
              <iframe
                title="Job Location"
                width="100%"
                height="100%"
                frameBorder="0"
                style={{ border: 0, pointerEvents: 'none' }}
                src={(() => {
                  const apiKey = 'AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8';
                  const dest = encodeURIComponent(
                    job.job_address || job.address_override || job.customer_address || job.address || 'United Kingdom'
                  );
                  return `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${dest}&zoom=15`;
                })()}
                allowFullScreen
              />
              {/* Tap-to-expand overlay — covers the whole mini-map. The
                  visual hint (top-right pill) makes the affordance
                  discoverable; the button itself is transparent so the
                  pin is fully visible underneath. */}
              <button
                type="button"
                onClick={() => setShowExpandedMap(true)}
                className="absolute inset-0 w-full h-full bg-transparent cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-blue-500/60"
                aria-label="Expand map"
                data-testid="expand-map-btn"
              >
                <span
                  className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/95 text-slate-700 text-[11px] font-semibold shadow-md pointer-events-none"
                >
                  <Maximize2 size={12} />
                  Expand
                </span>
              </button>
            </div>
            
            {/* Floating Directions Button */}
            <button 
              onClick={() => onNavigate && onNavigate(job)}
              className="absolute bottom-3 left-3 px-4 py-2 bg-white hover:bg-slate-50 rounded-full text-sm font-semibold text-slate-700 flex items-center gap-2 shadow-lg transition-all hover:scale-105"
              data-testid="get-directions-btn"
            >
              <Navigation size={16} className="text-blue-500" />
              Directions
            </button>
          </div>

          {/* Tiles Grid */}
          <div className="p-3 space-y-3">
            {/* Date Navigation - Job Schedule History */}
            {(() => {
              // Calculate job occurrence dates based on frequency
              const frequency = job.frequency || '';
              const currentDateStr = job.scheduled_date || job.next_due || job.date;
              
              // Parse frequency to get weeks interval. Supports:
              //  - "8 weeks", "8 weekly", "8w"
              //  - "every 6 weeks on Tuesday"
              //  - bare numbers like "6" (assumed to be weeks — matches the
              //    Squeegee import format)
              //  - "weekly", "fortnightly"/"bi-weekly", "monthly"
              const parseFrequencyWeeks = (freq) => {
                if (freq === null || freq === undefined) return null;
                const freqStr = String(freq).trim();
                if (!freqStr) return null;
                const freqLower = freqStr.toLowerCase();
                if (freqLower.includes('ad hoc') || freqLower.includes('one-off') || freqLower.includes('one off')) {
                  return null;
                }
                // "8 weeks", "every 6 weeks on Tuesday", etc.
                const match = freqLower.match(/(\d+)\s*w(?:eek)?s?/);
                if (match) return parseInt(match[1], 10);
                // Bare number ("6")
                const bare = freqLower.match(/^\s*(\d+)\s*$/);
                if (bare) return parseInt(bare[1], 10);
                if (freqLower.includes('weekly') && !freqLower.includes('bi')) return 1;
                if (freqLower.includes('fortnightly') || freqLower.includes('bi-weekly')) return 2;
                if (freqLower.includes('monthly')) return 4;
                return null;
              };
              
              const weeksInterval = parseFrequencyWeeks(frequency);
              const isRecurring = weeksInterval !== null && weeksInterval > 0;

              // One-off first cleans (canvassing imports) keep their recurrence
              // on a separate future job — borrow its schedule for the arrows.
              const siblingWeeks = (!isRecurring && siblingRecurring)
                ? parseFrequencyWeeks(siblingRecurring.frequency) : null;
              const siblingAnchor = (siblingWeeks && siblingRecurring?.scheduled_date)
                ? new Date(siblingRecurring.scheduled_date) : null;
              const navRecurring = isRecurring || !!siblingAnchor;
              
              // Generate occurrence dates: 5 past + current + 5 future
              const generateOccurrences = () => {
                if (!currentDateStr || !navRecurring) {
                  return [{ date: currentDateStr ? new Date(currentDateStr) : new Date(), isCurrent: true }];
                }

                // One-off with a sibling recurring job: this visit, then the
                // recurring job's date and its future occurrences.
                if (!isRecurring && siblingAnchor) {
                  const occurrences = [{ date: new Date(currentDateStr), isCurrent: true, isPast: false }];
                  for (let i = 0; i < 5; i++) {
                    const futureDate = new Date(siblingAnchor);
                    futureDate.setDate(futureDate.getDate() + (siblingWeeks * 7 * i));
                    occurrences.push({ date: futureDate, isCurrent: false, isPast: false });
                  }
                  return occurrences;
                }
                
                const currentDate = new Date(currentDateStr);
                const daysInterval = weeksInterval * 7;
                const occurrences = [];
                
                // Add 5 past occurrences
                for (let i = 5; i >= 1; i--) {
                  const pastDate = new Date(currentDate);
                  pastDate.setDate(pastDate.getDate() - (daysInterval * i));
                  occurrences.push({ date: pastDate, isCurrent: false, isPast: true });
                }
                
                // Add current occurrence
                occurrences.push({ date: currentDate, isCurrent: true, isPast: false });
                
                // Add 5 future occurrences
                for (let i = 1; i <= 5; i++) {
                  const futureDate = new Date(currentDate);
                  futureDate.setDate(futureDate.getDate() + (daysInterval * i));
                  occurrences.push({ date: futureDate, isCurrent: false, isPast: false });
                }
                
                return occurrences;
              };
              
              const occurrences = generateOccurrences();
              const currentIndex = occurrences.findIndex(o => o.isCurrent);
              const activeIndex = selectedDateIndex === 0 ? currentIndex : selectedDateIndex;
              const selectedOccurrence = occurrences[activeIndex] || occurrences[currentIndex];
              
              // Calculate relative time
              const getRelativeTime = (date, isFuture) => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const targetDate = new Date(date);
                targetDate.setHours(0, 0, 0, 0);
                const diffDays = Math.floor((targetDate - today) / (1000 * 60 * 60 * 24));
                const absDays = Math.abs(diffDays);
                const weeks = Math.floor(absDays / 7);
                const days = absDays % 7;
                
                if (diffDays === 0) return 'today';
                if (diffDays === 1) return 'tomorrow';
                if (diffDays === -1) return 'yesterday';
                
                if (diffDays < 0) {
                  if (weeks > 0 && days > 0) return `${weeks}w ${days}d ago`;
                  if (weeks > 0) return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
                  return `${days} day${days > 1 ? 's' : ''} ago`;
                } else {
                  if (weeks > 0 && days > 0) return `in ${weeks}w ${days}d`;
                  if (weeks > 0) return `in ${weeks} week${weeks > 1 ? 's' : ''}`;
                  return `in ${days} day${days > 1 ? 's' : ''}`;
                }
              };
              
              // Get the last done date
              const lastDoneStr = job.last_done || job.last_completed_date || job.completed_at;
              const lastDoneDate = lastDoneStr ? new Date(lastDoneStr) : null;
              
              return (
                <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                  <div className="flex items-stretch">
                    {/* Left Arrow - Go to earlier dates */}
                    <button 
                      onClick={() => setSelectedDateIndex(prev => {
                        const newIdx = (prev === 0 ? currentIndex : prev) - 1;
                        return Math.max(0, newIdx);
                      })}
                      disabled={activeIndex <= 0 || !navRecurring}
                      className="px-3 flex items-center justify-center bg-slate-50 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors border-r border-slate-100"
                      data-testid="date-nav-left"
                    >
                      <ChevronLeft size={20} className="text-slate-600" />
                    </button>
                    
                    {/* Date Content */}
                    <div className="flex-1 py-4">
                      <div className="grid grid-cols-2 divide-x divide-slate-100">
                        {/* Last Done */}
                        <div className="px-4 text-center">
                          <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Last Done</p>
                          {lastDoneDate ? (
                            <>
                              <p className="font-bold text-slate-900" data-testid="last-done-date">
                                {lastDoneDate.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: '2-digit' })}
                              </p>
                              <p className="text-xs text-slate-500">({getRelativeTime(lastDoneDate, false)})</p>
                              {job.last_done_was_skipped && (
                                <span
                                  data-testid="last-done-skipped-badge"
                                  className="inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-700"
                                >
                                  Skipped
                                </span>
                              )}
                            </>
                          ) : (
                            <p className="font-bold text-slate-400">Never</p>
                          )}
                        </div>
                        
                        {/* Selected/Next Due Date */}
                        <div className="px-4 text-center">
                          <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">
                            {selectedOccurrence?.isCurrent ? 'This Visit' : (selectedOccurrence?.isPast ? 'Past Visit' : 'Next Due')}
                          </p>
                          {selectedOccurrence ? (
                            <>
                              <p className={`font-bold ${selectedOccurrence.isCurrent ? 'text-emerald-600' : selectedOccurrence.isPast ? 'text-slate-600' : 'text-blue-600'}`} data-testid="next-due-date">
                                {selectedOccurrence.date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: '2-digit' })}
                              </p>
                              <p className="text-xs text-slate-500">({getRelativeTime(selectedOccurrence.date, !selectedOccurrence.isPast)})</p>
                            </>
                          ) : (
                            <p className="font-bold text-slate-400">{isRecurring ? 'Not set' : 'One-off'}</p>
                          )}
                        </div>
                      </div>
                      
                      {/* Navigation indicator */}
                      {navRecurring && (
                        <div className="flex justify-center gap-1 mt-2">
                          {occurrences.map((occ, idx) => (
                            <div 
                              key={idx}
                              className={`w-1.5 h-1.5 rounded-full transition-colors ${
                                idx === activeIndex 
                                  ? 'bg-emerald-500' 
                                  : occ.isCurrent 
                                    ? 'bg-emerald-200' 
                                    : 'bg-slate-200'
                              }`}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* Right Arrow - Go to later dates */}
                    <button 
                      onClick={() => setSelectedDateIndex(prev => {
                        const newIdx = (prev === 0 ? currentIndex : prev) + 1;
                        return Math.min(occurrences.length - 1, newIdx);
                      })}
                      disabled={activeIndex >= occurrences.length - 1 || !navRecurring}
                      className="px-3 flex items-center justify-center bg-slate-50 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors border-l border-slate-100"
                      data-testid="date-nav-right"
                    >
                      <ChevronRight size={20} className="text-slate-600" />
                    </button>
                  </div>
                </div>
              );
            })()}
            
            {/* Customer Tile - Full Width */}
            {/* Workers cannot view customer profile - show restricted view */}
            {isWorker ? (
              <div 
                className="w-full bg-white rounded-2xl p-4 flex items-center gap-4 shadow-sm"
                data-testid="customer-card-restricted"
              >
                <div 
                  className="w-14 h-14 rounded-2xl flex items-center justify-center text-white text-xl font-bold shadow-md"
                  style={{ 
                    background: `linear-gradient(135deg, ${getStaffColor ? getStaffColor(job.customer_name) : '#6366f1'} 0%, ${getStaffColor ? getStaffColor(job.customer_name) : '#4f46e5'}cc 100%)`
                  }}
                >
                  {(job.customer_name || 'U')[0].toUpperCase()}
                </div>
                <div className="flex-1 text-left">
                  <p className="font-bold text-lg text-slate-900">{job.customer_name || 'Unknown'}</p>
                  <p className="text-sm text-slate-400">Customer</p>
                </div>
                {/* Only show balance if worker has permission */}
                {workerPermissions.can_see_customer_balance !== false && (
                  <div className="text-right">
                    <p className="text-2xl font-bold text-slate-900">£{(customerData?.balance || job.customer_balance || 0).toFixed(2)}</p>
                    <p className="text-xs text-slate-400 uppercase tracking-wide">Balance</p>
                  </div>
                )}
              </div>
            ) : (
              <button 
                onClick={async () => {
                  const customerId = job.customerRef || job.customer_id;
                  if (!customerId) {
                    console.error('No customer ID available');
                    return;
                  }
                  setLoadingCustomer(true);
                  try {
                    const token = localStorage.getItem('streakfree_token');
                    const res = await fetch(`${API_URL}/api/customers/${customerId}`, {
                      headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (res.ok) {
                      const customer = await res.json();
                      setFullCustomerData(customer);
                      setShowCustomerProfile(true);
                    } else {
                      console.error('Failed to fetch customer');
                    }
                  } catch (err) {
                    console.error('Error fetching customer:', err);
                  } finally {
                    setLoadingCustomer(false);
                  }
                }}
                disabled={loadingCustomer}
                className="w-full bg-white rounded-2xl p-4 flex items-center gap-4 hover:shadow-lg transition-all active:scale-[0.98] shadow-sm disabled:opacity-70"
                data-testid="customer-card"
              >
                {loadingCustomer ? (
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center bg-slate-100">
                    <Loader2 size={24} className="text-slate-400 animate-spin" />
                  </div>
                ) : (
                  <div 
                    className="w-14 h-14 rounded-2xl flex items-center justify-center text-white text-xl font-bold shadow-md"
                    style={{ 
                      background: `linear-gradient(135deg, ${getStaffColor ? getStaffColor(job.customer_name) : '#6366f1'} 0%, ${getStaffColor ? getStaffColor(job.customer_name) : '#4f46e5'}cc 100%)`
                    }}
                  >
                    {(job.customer_name || 'U')[0].toUpperCase()}
                  </div>
                )}
                <div className="flex-1 text-left">
                  <p className="font-bold text-lg text-slate-900">{job.customer_name || 'Unknown'}</p>
                  <p className="text-sm text-slate-500">{loadingCustomer ? 'Loading...' : 'Tap to view profile'}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-slate-900">£{(customerData?.balance || job.customer_balance || 0).toFixed(2)}</p>
                  <p className="text-xs text-slate-400 uppercase tracking-wide">Balance</p>
                </div>
              </button>
            )}

            {/* Contact Buttons - Hide phone for workers without permission */}
            <div className="grid grid-cols-2 gap-3">
              {(customerData?.phone || job.customer_phone) && (!isWorker || workerPermissions.can_see_customer_phone !== false) && (
                <a 
                  href={`tel:${customerData?.phone || job.customer_phone}`}
                  className="py-4 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl text-white font-semibold flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 transition-all active:scale-95"
                  data-testid="call-btn"
                >
                  <Phone size={20} />
                  {customerData?.phone || job.customer_phone}
                </a>
              )}
              {(customerData?.email || job.customer_email) && (
                <a 
                  href={`mailto:${customerData?.email || job.customer_email}`}
                  className="py-4 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl text-white font-semibold flex items-center justify-center gap-2 shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all active:scale-95"
                  data-testid="email-btn"
                >
                  <Mail size={20} />
                  Email
                </a>
              )}
            </div>

            {/* Info Tiles - 2x2 Grid */}
            <div className="grid grid-cols-2 gap-3">
              {/* Status Tile */}
              <div className={`rounded-2xl p-4 ${
                isCompleted ? 'bg-gradient-to-br from-emerald-50 to-emerald-100 border-2 border-emerald-200' : 
                isPaid ? 'bg-gradient-to-br from-indigo-50 to-indigo-100 border-2 border-indigo-200' : 
                'bg-gradient-to-br from-amber-50 to-amber-100 border-2 border-amber-200'
              }`}>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${
                  isCompleted ? 'bg-emerald-500' : isPaid ? 'bg-indigo-500' : 'bg-amber-500'
                }`}>
                  {isCompleted ? <CheckCircle2 size={20} className="text-white" /> : 
                   isPaid ? <CreditCard size={20} className="text-white" /> : 
                   <Clock size={20} className="text-white" />}
                </div>
                <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Status</p>
                <p className={`text-lg font-bold ${
                  isCompleted ? 'text-emerald-700' : isPaid ? 'text-indigo-700' : 'text-amber-700'
                }`}>
                  {isPaid ? 'Paid' : isCompleted ? 'Done' : 'Scheduled'}
                </p>
              </div>

              {/* Service Tile - Workers can only view */}
              {isWorker ? (
                <div className="rounded-2xl p-4 bg-white shadow-sm border border-slate-100 text-left">
                  <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center mb-3">
                    <Briefcase size={20} className="text-violet-600" />
                  </div>
                  <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Service</p>
                  <p className="text-lg font-bold text-slate-900 truncate">{job.services || 'Window Clean'}</p>
                </div>
              ) : (
                <button 
                  onClick={() => handleEdit('services')}
                  className="rounded-2xl p-4 bg-white shadow-sm hover:shadow-lg transition-all text-left active:scale-95 border border-slate-100"
                >
                  <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center mb-3">
                    <Briefcase size={20} className="text-violet-600" />
                  </div>
                  <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Service</p>
                  <p className="text-lg font-bold text-slate-900 truncate">{job.services || 'Window Clean'}</p>
                </button>
              )}

              {/* Price Tile */}
              {!isWorker && (
                <button 
                  onClick={() => handleEdit('price')}
                  className="rounded-2xl p-4 bg-gradient-to-br from-slate-800 to-slate-900 shadow-lg hover:shadow-xl transition-all text-left active:scale-95"
                >
                  <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center mb-3">
                    <PoundSterling size={20} className="text-white" />
                  </div>
                  <p className="text-xs uppercase tracking-wide text-slate-400 mb-1">Price</p>
                  <p className="text-2xl font-bold text-white">£{(job.price || 0).toFixed(2)}</p>
                </button>
              )}

              {/* Assignee Tile - Workers can only view, not edit */}
              {isWorker ? (
                <div className="rounded-2xl p-4 bg-white shadow-sm border border-slate-100 text-left">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center mb-3">
                    <User size={20} className="text-blue-600" />
                  </div>
                  <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Assignee</p>
                  <p className="text-lg font-bold text-slate-900 truncate">{job.assignee || 'Unassigned'}</p>
                </div>
              ) : (
                <button 
                  onClick={() => handleEdit('assignee')}
                  className="rounded-2xl p-4 bg-white shadow-sm hover:shadow-lg transition-all text-left active:scale-95 border border-slate-100"
                >
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center mb-3">
                    <User size={20} className="text-blue-600" />
                  </div>
                  <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Assignee</p>
                  <p className="text-lg font-bold text-slate-900 truncate">{job.assignee || 'Unassigned'}</p>
                </button>
              )}
            </div>

            {/* Schedule Tiles Row - Workers can only view, not edit */}
            <div className="grid grid-cols-2 gap-3">
              {/* Round Tile */}
              {isWorker ? (
                <div className="rounded-2xl p-4 bg-white shadow-sm border border-slate-100 text-left">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center mb-3">
                    <MapPin size={20} className="text-emerald-600" />
                  </div>
                  <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Round</p>
                  <p className="text-lg font-bold text-slate-900 truncate">{(() => {
                    // Normalise the literal sentinel "Unassigned" → "No round"
                    // so it doesn't get visually confused with "no worker
                    // assigned" (a separate concept). See WorkPlanner card pill
                    // comment for the full rationale.
                    const raw = job.round_name || job.customer_zone || job.zone;
                    if (!raw || String(raw).trim().toLowerCase() === 'unassigned') return 'No round';
                    return raw;
                  })()}</p>
                </div>
              ) : (
                <button 
                  onClick={() => handleEdit('round')}
                  className="rounded-2xl p-4 bg-white shadow-sm hover:shadow-lg transition-all text-left active:scale-95 border border-slate-100"
                >
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center mb-3">
                    <MapPin size={20} className="text-emerald-600" />
                  </div>
                  <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Round</p>
                  <p className="text-lg font-bold text-slate-900 truncate">{(() => {
                    const raw = job.round_name || job.customer_zone || job.zone;
                    if (!raw || String(raw).trim().toLowerCase() === 'unassigned') return 'No round';
                    return raw;
                  })()}</p>
                </button>
              )}

              {/* Frequency Tile */}
              {isWorker ? (
                <div className="rounded-2xl p-4 bg-white shadow-sm border border-slate-100 text-left">
                  <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center mb-3">
                    <RefreshCw size={20} className="text-orange-600" />
                  </div>
                  <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Frequency</p>
                  <p className="text-lg font-bold text-slate-900 truncate">{job.frequency || 'One-off'}</p>
                </div>
              ) : (
                <button 
                  onClick={() => handleEdit('frequency')}
                  className="rounded-2xl p-4 bg-white shadow-sm hover:shadow-lg transition-all text-left active:scale-95 border border-slate-100"
                >
                  <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center mb-3">
                    <RefreshCw size={20} className="text-orange-600" />
                  </div>
                  <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Frequency</p>
                  <p className="text-lg font-bold text-slate-900 truncate">{job.frequency || 'One-off'}</p>
                </button>
              )}
            </div>

            {/* Customer Photos Tile (under Round/Frequency) */}
            {(job?.customerRef || job?.customer_id || job?.customerId) && (
              <CustomerPhotos
                customerId={job.customerRef || job.customer_id || job.customerId}
                title="Photos"
                readOnly={isWorker && workerPermissions?.can_edit_customer === false}
              />
            )}

            {/* Customer Notes — same notes that appear on the customer card.
                We deliberately DON'T pass initialNotes here so the editor
                auto-fetches from /api/customers/{id}. The job object doesn't
                carry customer.notes, so any prop value we'd pass would be
                a stale empty string and the editor would skip the fetch. */}
            {(job?.customerRef || job?.customer_id || job?.customerId) && (
              <CustomerNotesEditor
                customerId={job.customerRef || job.customer_id || job.customerId}
                title="Customer Notes"
                placeholder="Gate codes, dog warnings, where the tap is — anything the next visit should know"
              />
            )}

            {/* Job-level Notes Tile (legacy: a per-job note, e.g. one-off
                instructions for this specific visit). Different from the
                customer-level notes above. */}
            {job.notes && (
              <div className="rounded-2xl p-4 bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-amber-400 flex items-center justify-center">
                    <FileText size={16} className="text-white" />
                  </div>
                  <p className="font-bold text-amber-800">Job Notes</p>
                </div>
                <p className="text-amber-700">{job.notes}</p>
              </div>
            )}

            {/* Invoice Tile */}
            {isCompleted && invoiceData && (
              <button 
                onClick={() => onViewInvoice && onViewInvoice(invoiceData)}
                className="w-full rounded-2xl p-4 bg-gradient-to-r from-indigo-500 to-purple-500 shadow-lg hover:shadow-xl transition-all active:scale-98 flex items-center gap-4"
                data-testid="view-invoice-btn"
              >
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                  <Receipt size={24} className="text-white" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-bold text-white text-lg">Invoice #{invoiceData.invoice_number}</p>
                  <p className="text-indigo-100 text-sm">
                    {invoiceData.status === 'paid' ? '✓ Paid' : `Owing: £${invoiceData.total?.toFixed(2)}`}
                  </p>
                </div>
                <ChevronRight size={24} className="text-white/70" />
              </button>
            )}
          </div>

          {/* Spacer */}
          <div className="h-24" />
        </div>

        {/* Bottom Action Buttons - Floating style */}
        {(isCompleted || showActions) && (
        <div className="flex-shrink-0 p-4 bg-white/80 backdrop-blur-xl border-t border-slate-200/50">
          {isCompleted ? (
            <div className="flex gap-3">
              {invoiceData && (
              <button
                onClick={() => onViewInvoice && onViewInvoice(invoiceData)}
                className="flex-1 py-4 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-2xl font-bold flex items-center justify-center gap-2 shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/50 transition-all active:scale-95"
              >
                <Receipt size={20} />
                View Invoice
              </button>
              )}
              <button
                onClick={() => onUndoComplete && onUndoComplete(job)}
                className="py-4 px-5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-2xl font-bold transition-all active:scale-95"
              >
                Undo
              </button>
            </div>
          ) : showActions ? (
            <div className="flex gap-2">
              <button
                onClick={handleDoneClick}
                className="flex-1 py-3.5 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-xl font-bold flex items-center justify-center gap-1.5 shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50 transition-all active:scale-95"
                data-testid="done-btn"
              >
                <Check size={18} strokeWidth={3} />
                Done
              </button>
              <button
                onClick={() => setShowSkipModal(true)}
                className="flex-1 py-3.5 bg-gradient-to-r from-amber-400 to-amber-500 text-white rounded-xl font-bold flex items-center justify-center gap-1.5 shadow-lg shadow-amber-500/30 hover:shadow-amber-500/50 transition-all active:scale-95"
                data-testid="skip-btn"
              >
                <SkipForward size={18} />
                Skip
              </button>
              {/* Hide payment option for workers */}
              {!isWorker && (
                <button
                  onClick={() => setShowPaymentModal(true)}
                  className="flex-1 py-3.5 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white rounded-xl font-bold flex items-center justify-center gap-1.5 shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/50 transition-all active:scale-95"
                  data-testid="done-paid-btn"
                >
                  <CreditCard size={18} />
                  Done & Paid
                </button>
              )}
            </div>
          ) : null}
        </div>
        )}

        {/* Modals */}
        {renderEditModal()}
        {showPaymentModal && <PaymentMethodModal />}

        {/* Charge confirmation modal — mirrors WorkPlanner SwipeableJobCard */}
        {showChargeConfirmModal && (
          <div
            className="fixed inset-0 z-[70] flex items-end justify-center bg-black/50 backdrop-blur-sm"
            onClick={() => setShowChargeConfirmModal(false)}
            data-testid="charge-confirm-overlay"
          >
            <div
              className="bg-white rounded-t-3xl w-full max-w-md shadow-2xl animate-slide-up"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-center pt-3 pb-2">
                <div className="w-10 h-1 bg-slate-300 rounded-full" />
              </div>
              <div className="px-6 pt-2 pb-4 border-b border-slate-100">
                <h3 className="font-bold text-xl text-slate-900">Confirm payment</h3>
                <p className="text-sm text-slate-500 mt-1">
                  {paymentTypeLabel === 'Stripe'
                    ? 'Card on file will be charged'
                    : 'Direct debit will be collected'}
                </p>
              </div>

              <div className="p-6 space-y-4">
                <div className="bg-slate-50 rounded-2xl p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                      <CreditCard size={22} className="text-emerald-700" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">{job.customer_name}</p>
                      <p className="text-xs text-slate-500">{paymentTypeLabel}</p>
                    </div>
                  </div>

                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Amount to charge (inc. VAT):
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium">£</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={chargeAmount.toFixed(2)}
                      onChange={(e) => setChargeAmount(parseFloat(e.target.value) || 0)}
                      className="w-full pl-8 pr-4 py-3 text-lg font-bold text-slate-900 bg-white border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      data-testid="jdp-charge-amount-input"
                    />
                  </div>
                  {vatSettings.enabled && (
                    <div className="mt-2 text-xs text-slate-500 space-y-1">
                      <div className="flex justify-between">
                        <span>Job price:</span>
                        <span>£{(job.price || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>VAT ({vatSettings.percentage}%):</span>
                        <span>£{((job.price || 0) * vatSettings.percentage / 100).toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                  {chargeAmount.toFixed(2) !== calculateTotalWithVat(job.price || 0).toFixed(2) && (
                    <p className="text-xs text-amber-600 mt-1">
                      Original total: £{calculateTotalWithVat(job.price || 0).toFixed(2)}
                    </p>
                  )}
                </div>
              </div>

              <div className="px-6 pb-6 space-y-2">
                <button
                  onClick={handleConfirmCharge}
                  className="w-full flex items-center justify-center gap-2 py-4 bg-emerald-500 text-white rounded-xl font-semibold hover:bg-emerald-600 transition-colors"
                  data-testid="jdp-confirm-charge-btn"
                >
                  <CheckCircle2 size={20} />
                  Confirm &amp; Charge £{chargeAmount.toFixed(2)}
                </button>

                <div className="flex items-center gap-2 py-2">
                  <div className="flex-1 h-px bg-slate-200" />
                  <span className="text-xs text-slate-400 font-medium">or send invoice</span>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>

                <div className="flex gap-2">
                  {(customerData?.email || job.customer_email) && (
                    <button
                      onClick={() => handleSendInsteadOfCharge('email')}
                      className="flex-1 flex items-center justify-center gap-2 py-3 bg-blue-500 text-white rounded-xl font-medium hover:bg-blue-600 transition-colors"
                      data-testid="jdp-send-email-btn"
                    >
                      <Mail size={18} />
                      Email
                    </button>
                  )}
                  {(customerData?.phone || job.customer_phone) && (
                    <button
                      onClick={() => handleSendInsteadOfCharge('sms')}
                      className="flex-1 flex items-center justify-center gap-2 py-3 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-colors"
                      data-testid="jdp-send-sms-btn"
                    >
                      <MessageSquare size={18} />
                      SMS
                    </button>
                  )}
                  {(customerData?.email || job.customer_email) && (customerData?.phone || job.customer_phone) && (
                    <button
                      onClick={() => handleSendInsteadOfCharge('both')}
                      className="flex-1 flex items-center justify-center gap-2 py-3 bg-purple-500 text-white rounded-xl font-medium hover:bg-purple-600 transition-colors"
                      data-testid="jdp-send-both-btn"
                    >
                      <Send size={18} />
                      Both
                    </button>
                  )}
                </div>

                <button
                  onClick={() => setShowChargeConfirmModal(false)}
                  className="w-full py-3 text-slate-500 font-medium hover:text-slate-700 transition-colors"
                  data-testid="jdp-charge-cancel-btn"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Invoice-channel chooser — shown when the customer has no saved
            payment method on `Done` press. Lets owner OR worker
            (`can_take_payments` is irrelevant here — no auto-charge attempted)
            pick which channel(s) deliver the invoice. */}
        {showInvoiceSendModal && (
          <div
            className="fixed inset-0 z-[70] flex items-end justify-center bg-black/50 backdrop-blur-sm"
            onClick={() => setShowInvoiceSendModal(false)}
            data-testid="jdp-invoice-send-overlay"
          >
            <div
              className="bg-white rounded-t-3xl w-full max-w-md shadow-2xl animate-slide-up"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-center pt-3 pb-2">
                <div className="w-10 h-1 bg-slate-300 rounded-full" />
              </div>
              <div className="px-6 pt-2 pb-4">
                <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <CheckCircle2 size={32} className="text-emerald-600" />
                </div>
                <h3 className="font-bold text-xl text-slate-900 text-center">Mark as Complete?</h3>
                <p className="text-sm text-slate-500 text-center mt-1">
                  {job.customer_name}
                </p>
              </div>

              <div className="px-6 pb-4">
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <PoundSterling size={20} className="text-amber-600" />
                    <div>
                      <p className="font-medium text-amber-900">No payment method</p>
                      <p className="text-sm text-amber-700">
                        Invoice will be created and added to Owing list
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-6 pb-6">
                <p className="text-sm font-medium text-slate-700 mb-3">Send invoice via:</p>
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {(customerData?.phone || job.customer_phone) && (
                    <button
                      onClick={() => handleSendInvoiceAndComplete('sms')}
                      className="flex flex-col items-center justify-center gap-1.5 py-3 bg-[#25d366] text-white rounded-xl font-medium hover:bg-[#1ebe5b] transition-colors"
                      data-testid="jdp-invoice-send-whatsapp"
                      title="Sends WhatsApp if available, falls back to SMS automatically"
                    >
                      <MessageSquare size={20} />
                      <span className="text-sm">WhatsApp</span>
                    </button>
                  )}
                  {(customerData?.email || job.customer_email) && (
                    <button
                      onClick={() => handleSendInvoiceAndComplete('email')}
                      className="flex flex-col items-center justify-center gap-1.5 py-3 bg-blue-500 text-white rounded-xl font-medium hover:bg-blue-600 transition-colors"
                      data-testid="jdp-invoice-send-email"
                    >
                      <Mail size={20} />
                      <span className="text-sm">Email</span>
                    </button>
                  )}
                  {(customerData?.email || job.customer_email) && (customerData?.phone || job.customer_phone) && (
                    <button
                      onClick={() => handleSendInvoiceAndComplete('both')}
                      className="flex flex-col items-center justify-center gap-1.5 py-3 bg-purple-500 text-white rounded-xl font-medium hover:bg-purple-600 transition-colors"
                      data-testid="jdp-invoice-send-both"
                    >
                      <Send size={18} />
                      <span className="text-sm">Both</span>
                    </button>
                  )}
                </div>
                <p className="text-[11px] text-slate-500 text-center mb-3 -mt-1">
                  WhatsApp auto-falls back to SMS if the customer isn&apos;t on WhatsApp.
                </p>
                <button
                  onClick={() => handleSendInvoiceAndComplete('none')}
                  className="w-full py-3 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200 transition-colors"
                  data-testid="jdp-invoice-send-none"
                >
                  Don&apos;t Send
                </button>
                <button
                  onClick={() => setShowInvoiceSendModal(false)}
                  className="w-full py-3 mt-2 text-slate-500 font-medium hover:text-slate-700 transition-colors"
                  data-testid="jdp-invoice-send-cancel"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Expanded Map Modal — full-viewport view of the job location.
            Opened by tapping the mini-map at the top of the panel. Uses
            the same /place embed but at zoom=17 (one click closer than
            the mini-map) so the user can actually see neighbouring
            properties, parking spots, access points, etc.

            UX choices:
              * Full screen on mobile (z-[80] is above every other modal
                in this panel) so it really does feel like "expand the map".
              * Tap the dim backdrop OR the close button to dismiss.
                We DON'T close on iframe click — the user needs to be
                able to pan/zoom inside it.
              * Directions + Address summary pinned at the bottom so the
                user keeps the key info visible while exploring the map.
        */}
        {showExpandedMap && (
          <div
            className="fixed inset-0 z-[80] flex flex-col bg-black"
            onClick={() => { setShowExpandedMap(false); setMapMode('place'); }}
            data-testid="expanded-map-overlay"
          >
            {/* Close button — top right, above everything. Outside the
                iframe-wrapping div so backdrop click still works. */}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setShowExpandedMap(false); setMapMode('place'); }}
              className="absolute top-4 right-4 z-10 w-11 h-11 rounded-full bg-white/95 text-slate-900 shadow-lg flex items-center justify-center hover:bg-white active:scale-95 transition-all"
              aria-label="Close expanded map"
              data-testid="expanded-map-close"
            >
              <X size={22} strokeWidth={2.5} />
            </button>

            {/* Address pill — top centre. Tells the user what address
                this map is showing without making them scroll back. */}
            <div className="absolute top-4 left-4 right-20 z-10 px-3 py-2 rounded-xl bg-white/95 shadow-lg flex items-center gap-2 pointer-events-none">
              <MapPin size={16} className="text-blue-500 flex-shrink-0" />
              <span className="text-sm font-semibold text-slate-900 truncate">
                {job.job_address || job.address_override || job.customer_address || job.address || 'Job location'}
              </span>
            </div>

            <div className="flex-1 relative" onClick={(e) => e.stopPropagation()}>
              <iframe
                key={mapMode}
                title={mapMode === 'streetview' ? 'Job Location (street view)' : 'Job Location (expanded)'}
                width="100%"
                height="100%"
                frameBorder="0"
                style={{ border: 0 }}
                src={(() => {
                  const apiKey = 'AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8';
                  if (mapMode === 'streetview') {
                    // Street View embed REQUIRES coordinates — it cannot
                    // geocode a free-text address. We feed it the
                    // customer's saved lat/lng (set when the address
                    // picker resolved the place). Heading/pitch/FOV are
                    // sensible defaults; Google then lets the user spin
                    // freely inside the embed.
                    const lat = customerData?.latitude;
                    const lng = customerData?.longitude;
                    if (lat != null && lng != null) {
                      return `https://www.google.com/maps/embed/v1/streetview?key=${apiKey}&location=${lat},${lng}&heading=210&pitch=10&fov=80`;
                    }
                    // Coords missing — fall through to the place view so
                    // the user isn't stuck with a blank panel. The Street
                    // View button itself is disabled below in this case.
                  }
                  const dest = encodeURIComponent(
                    job.job_address || job.address_override || job.customer_address || job.address || 'United Kingdom'
                  );
                  // zoom=17 is one step closer than the mini-map (zoom=15)
                  // — close enough to identify the actual property + a
                  // few neighbours, but not so close the user loses
                  // orientation.
                  return `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${dest}&zoom=17`;
                })()}
                allowFullScreen
              />
            </div>

            {/* Bottom action bar. Order (left → right): map-mode toggle,
                Directions (primary), Close.
                The map/street-view toggle uses a single "swap" button
                rather than two pills so it stays compact on a phone.
                When we don't have coords for street view (very rare for
                customers added via the picker, common for legacy CSV
                imports), the toggle is hidden — we never show a button
                that does nothing. */}
            <div className="bg-white px-4 py-3 flex items-center gap-2 shadow-2xl">
              {(customerData?.latitude != null && customerData?.longitude != null) && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMapMode((m) => (m === 'streetview' ? 'place' : 'streetview'));
                  }}
                  className="px-3 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold transition-colors active:scale-95 flex items-center gap-1.5"
                  data-testid="expanded-map-toggle-mode"
                  aria-label={mapMode === 'streetview' ? 'Switch to map view' : 'Switch to street view'}
                  title={mapMode === 'streetview' ? 'Map view' : 'Street View'}
                >
                  {mapMode === 'streetview' ? (
                    <>
                      <MapPin size={18} />
                      <span className="text-sm">Map</span>
                    </>
                  ) : (
                    <>
                      <User size={18} />
                      <span className="text-sm">Street</span>
                    </>
                  )}
                </button>
              )}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowExpandedMap(false);
                  setMapMode('place');
                  onNavigate && onNavigate(job);
                }}
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-semibold transition-colors active:scale-95"
                data-testid="expanded-map-directions"
              >
                <Navigation size={18} />
                Directions
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setShowExpandedMap(false); setMapMode('place'); }}
                className="px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold transition-colors active:scale-95"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Skip Modal */}
        {showSkipModal && (
          <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowSkipModal(false)}>
            <div className="bg-white rounded-t-3xl w-full max-w-md shadow-2xl animate-slide-up" onClick={e => e.stopPropagation()}>
              <div className="flex justify-center pt-3 pb-2">
                <div className="w-10 h-1 bg-slate-300 rounded-full" />
              </div>
              <div className="px-5 py-4 border-b border-slate-100">
                <h3 className="font-bold text-lg text-slate-900">Skip this job?</h3>
                <p className="text-sm text-slate-500 mt-1">Select a reason for skipping</p>
              </div>
              <div className="p-4 space-y-2 max-h-64 overflow-y-auto">
                {[
                  'Customer unavailable',
                  'Weather conditions',
                  'Access blocked',
                  'Customer cancelled',
                  'Running late',
                  'Other'
                ].map((reason) => (
                  <button
                    key={reason}
                    onClick={() => {
                      setShowSkipModal(false);
                      onSkip && onSkip(job, reason);
                    }}
                    className="w-full px-4 py-3 text-left rounded-xl bg-slate-50 hover:bg-amber-50 text-slate-700 font-medium transition-colors"
                  >
                    {reason}
                  </button>
                ))}
              </div>
              <div className="px-5 py-3 border-t border-slate-100">
                <button
                  onClick={() => setShowSkipModal(false)}
                  className="w-full py-2.5 text-slate-500 font-medium hover:text-slate-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Job Edit Modal */}
        {showEditJobModal && (
          <JobEditModal
            job={job}
            onClose={() => setShowEditJobModal(false)}
            onSave={(updatedJob) => {
              setShowEditJobModal(false);
              // Update the job in the parent - refresh data
              if (onUpdateJob) {
                onUpdateJob(job.id, updatedJob, false);
              }
            }}
            onDelete={(jobId) => {
              setShowEditJobModal(false);
              onClose();
            }}
          />
        )}
        
        {/* Full Customer Profile Modal */}
        {showCustomerProfile && fullCustomerData && (
          <CustomerDetailModal
            customer={fullCustomerData}
            job={job}
            onClose={() => {
              setShowCustomerProfile(false);
              setFullCustomerData(null);
            }}
          />
        )}
      </div>
    </>
  );
};

export default JobDetailPanel;
